import type { KeyboardEvent, PointerEvent } from 'react';
import { useState } from 'react';

import { cn } from '@/lib/utils';

/**
 * The pieces `BarChart` and `LineChart` share — series, scale, axis ticks,
 * legend, tooltip, the screen-reader table and the hover/keyboard cursor —
 * written once so the two charts cannot drift apart (CLAUDE.md §3).
 *
 * Colours are the `series-*` tokens, validated for colour-blind separation and
 * contrast in both themes (see `styles/index.css`). Text never takes a series
 * colour; a short key beside it carries identity.
 */

/** The three chart series, in their fixed order. Never cycled. */
export const SERIES_TONES = {
  1: { fill: 'fill-series-1', stroke: 'stroke-series-1', bg: 'bg-series-1' },
  2: { fill: 'fill-series-2', stroke: 'stroke-series-2', bg: 'bg-series-2' },
  3: { fill: 'fill-series-3', stroke: 'stroke-series-3', bg: 'bg-series-3' },
  4: { fill: 'fill-series-4', stroke: 'stroke-series-4', bg: 'bg-series-4' },
} as const;

export type SeriesTone = keyof typeof SERIES_TONES;

export interface ChartSeries<K extends string> {
  key: K;
  label: string;
  tone: SeriesTone;
}

/** One x position: its label and a value per series (null = no data). */
export interface ChartPoint<K extends string> {
  label: string;
  /** Shorter label for the axis, when the full one is long ("Mon 6 Oct" → "6"). */
  axisLabel?: string;
  values: Record<K, number | null>;
}

/**
 * A round top for the y axis and the gridlines under it: 0, 25, 50 …
 *
 * `integer` keeps every step a whole number — for counts, where an axis
 * reading 0, 0.5, 1 offers half a login. The charts pass it themselves when
 * every value they draw is whole, so callers never have to.
 */
export function niceScale(max: number, tickCount = 4, integer = false): { top: number; ticks: number[] } {
  if (max <= 0) return { top: tickCount, ticks: Array.from({ length: tickCount + 1 }, (_, i) => i) };

  const rough = max / tickCount;
  const magnitude = 10 ** Math.floor(Math.log10(rough));
  const candidates = [1, 2, 2.5, 5, 10]
    .map((m) => m * magnitude)
    .filter((candidate) => !integer || Number.isInteger(candidate));
  const found = candidates.find((candidate) => candidate >= rough) ?? candidates.at(-1) ?? rough;
  const step = integer ? Math.max(1, found) : found;
  // As many steps as the data needs — a top of 2 with steps of 1 is two
  // gridlines, not four with the upper half empty.
  const count = Math.max(1, Math.ceil(max / step));

  return { top: step * count, ticks: Array.from({ length: count + 1 }, (_, i) => i * step) };
}

/** Whether every value a chart draws is a whole number — counts, not averages. */
export function allWhole(values: readonly (number | null)[]): boolean {
  return values.every((value) => value === null || Number.isInteger(value));
}

/** Which x labels to print so they never collide: roughly one per `minGap` px. */
export function labelEvery(count: number, width: number, minGap = 44): number {
  if (count === 0 || width === 0) return 1;
  return Math.max(1, Math.ceil(count / Math.max(1, Math.floor(width / minGap))));
}

/** Plot margins: room for the y labels on the left and x labels underneath. */
export const PLOT = { top: 8, right: 8, bottom: 24, left: 36 } as const;

/** A `text-2xs` character, generously — wide enough for digits and letters. */
const Y_LABEL_CHAR_PX = 6;
/** The space between a y label and the plot (`x={left - 6}`), plus a little air. */
const Y_LABEL_GAP_PX = 8;

/**
 * The left margin that fits the widest y label, and never less than `PLOT.left`.
 *
 * A fixed 36px held "1,000" but not "1 hr 30 mins": the SVG draws past its own
 * edge, so longer labels ran out of the card. Charts whose labels fit are laid
 * out exactly as before.
 */
export function plotLeftFor(labels: readonly string[]): number {
  const longest = Math.max(0, ...labels.map((label) => label.length));
  return Math.max(PLOT.left, Math.ceil(longest * Y_LABEL_CHAR_PX + Y_LABEL_GAP_PX));
}

/**
 * Whether the x label at `index` is printed, one in every `every`.
 *
 * Counted from the first point by default. `fromEnd` counts back from the last,
 * so a timeline always names its latest day — counted from the start, the last
 * label of a thirty-day chart fell a day or two short of today, and the newest
 * days read as missing.
 */
export function showsAxisLabel(index: number, count: number, every: number, fromEnd = false): boolean {
  return (fromEnd ? count - 1 - index : index) % every === 0;
}

/**
 * The active x position — set by the pointer or by the arrow keys, so the
 * tooltip is reachable without a mouse.
 */
export function useChartCursor(count: number, plotLeft: number, step: number) {
  const [active, setActive] = useState<number | null>(null);

  function onPointerMove(event: PointerEvent<HTMLElement>) {
    const box = event.currentTarget.getBoundingClientRect();
    const index = Math.floor((event.clientX - box.left - plotLeft) / step);
    setActive(index >= 0 && index < count ? index : null);
  }

  function onKeyDown(event: KeyboardEvent<HTMLElement>) {
    if (count === 0) return;
    if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
      event.preventDefault();
      const delta = event.key === 'ArrowRight' ? 1 : -1;
      setActive((current) => Math.min(count - 1, Math.max(0, (current ?? (delta > 0 ? -1 : count)) + delta)));
    } else if (event.key === 'Home' || event.key === 'End') {
      event.preventDefault();
      setActive(event.key === 'Home' ? 0 : count - 1);
    } else if (event.key === 'Escape') {
      setActive(null);
    }
  }

  return {
    active,
    handlers: {
      onPointerMove,
      onPointerLeave: () => setActive(null),
      onKeyDown,
      onBlur: () => setActive(null),
    },
  };
}

/** The legend, above the plot. Shown for two series or more; one needs none. */
export function ChartLegend<K extends string>({ series }: { series: ChartSeries<K>[] }) {
  if (series.length < 2) return null;

  return (
    <ul className="flex flex-wrap items-center gap-x-4 gap-y-1">
      {series.map((item) => (
        <li key={item.key} className="flex items-center gap-1.5 text-xs text-ink-2">
          <span aria-hidden className={cn('h-0.5 w-3 rounded-pill', SERIES_TONES[item.tone].bg)} />
          {item.label}
        </li>
      ))}
    </ul>
  );
}

/**
 * The readout for one x position: its label, then every series — value first,
 * label after, keyed by a short line in the series colour.
 */
export function ChartTooltip<K extends string>({
  point,
  series,
  formatValue,
  x,
  width,
  total,
}: {
  point: ChartPoint<K>;
  series: ChartSeries<K>[];
  formatValue: (value: number) => string;
  /** Centre of the active position, in px from the chart's left edge. */
  x: number;
  /** The chart's width, to keep the tooltip inside it. */
  width: number;
  /** A total line under the series, for stacked bars. */
  total?: number;
}) {
  const flipLeft = x > width / 2;

  return (
    <div
      role="status"
      className={cn(
        'pointer-events-none absolute top-0 z-10 min-w-[9rem] rounded-field border border-line bg-surface px-3 py-2 shadow-card',
        flipLeft ? '-translate-x-full' : '',
      )}
      // Positioned by measurement; Tailwind cannot express a computed offset.
      style={{ left: flipLeft ? x - 12 : x + 12 }}
    >
      <p className="text-xs font-[650] text-ink">{point.label}</p>
      <ul className="mt-1 flex flex-col gap-0.5">
        {series.map((item) => {
          const value = point.values[item.key];
          return (
            <li key={item.key} className="flex items-center gap-2 text-xs">
              <span aria-hidden className={cn('h-0.5 w-3 shrink-0 rounded-pill', SERIES_TONES[item.tone].bg)} />
              <span className="font-[650] tabular-nums text-ink">{value === null ? '—' : formatValue(value)}</span>
              <span className="text-ink-3">{item.label}</span>
            </li>
          );
        })}
        {total !== undefined && (
          <li className="mt-0.5 flex items-center gap-2 border-t border-line pt-0.5 text-xs">
            <span aria-hidden className="w-3 shrink-0" />
            <span className="font-[650] tabular-nums text-ink">{formatValue(total)}</span>
            <span className="text-ink-3">in all</span>
          </li>
        )}
      </ul>
    </div>
  );
}

/** The same numbers as a table, for screen readers. The drawing is `aria-hidden`. */
export function ChartDataTable<K extends string>({
  caption,
  points,
  series,
  formatValue,
}: {
  caption: string;
  points: ChartPoint<K>[];
  series: ChartSeries<K>[];
  formatValue: (value: number) => string;
}) {
  return (
    <table className="sr-only">
      <caption>{caption}</caption>
      <thead>
        <tr>
          <th scope="col">Date</th>
          {series.map((item) => (
            <th key={item.key} scope="col">
              {item.label}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {points.map((point) => (
          <tr key={point.label}>
            <th scope="row">{point.label}</th>
            {series.map((item) => {
              const value = point.values[item.key];
              return <td key={item.key}>{value === null ? 'No data' : formatValue(value)}</td>;
            })}
          </tr>
        ))}
      </tbody>
    </table>
  );
}
